import { doc, getDoc, getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { DbCollection } from "../constants/db";
import { BatchesActions } from "../store/reducers/batches/batch_reducer";
import { PartnerActions } from "../store/reducers/partner/selected";
import { AppDispatch } from "../store/store";
import Partner, { PartnerDbData } from "../models/Partner";
import { SortingRulesActions } from "../store/reducers/sorting_rules/sorting_rules";
import { PartnersActions } from "../store/reducers/partner/list";
import { fetchAPI } from "./actions";
import urls from "../constants/urls";
import { showError } from "../store/reducers/snacks";

/**
  * Retrieves the list of partners the user can access.
 */
const listPartners = () => async (dispatch: AppDispatch) => {
    dispatch(PartnersActions.startLoadingList());

    try {
        const partners: Partner[] = await fetchAPI(urls.HTTP_FUNCTIONS.listPartners);
        dispatch(PartnersActions.setList(partners));        
        return partners;
    }
    catch (e) {
        const error = e as Error;
        console.error("Failed loading partners");
        console.error(error);
        dispatch(PartnersActions.setError(error.message));
        dispatch(showError(error.message));
        return [];
    }
};

/**
 * Fetches the partner document from Firestore.
 * @param userPartnerID - The userPartnerID to query for the partner document.
 * @returns A thunk function for Redux to execute.
 */
export const fetchPartnerDoc = (userPartnerID: string) => async (dispatch: AppDispatch) => {
    dispatch(BatchesActions.startLoading());
    dispatch(BatchesActions.clearError());

    try {
        const db = getFirestore();
        const partnerDocRef = doc(db, DbCollection.PARTNERS, userPartnerID);
        const partnerDocSnap = await getDoc(partnerDocRef);

        if (partnerDocSnap.exists()) {
            const partnerData = partnerDocSnap.data() as PartnerDbData;
            const viewport = partnerData.defaultMapViewport;
            const partner: Partner = {
                ...partnerData,
                ID: userPartnerID,
                defaultMapViewport: viewport ? {
                    center: { latitude: viewport.center.latitude, longitude: viewport.center.longitude },
                    zoom: viewport.zoom,
                } : undefined,
            };
            dispatch(PartnerActions.setPartner(partner));
            
            // apply the partner's specific sorting rules
            dispatch(SortingRulesActions.setRules(partner.sortingRules));

        } else {
            dispatch(BatchesActions.setError('Partner document not found.'));
        }
    } catch (error) {
        dispatch(BatchesActions.setError(JSON.stringify(error, null, 2)));
    } finally {
        dispatch(BatchesActions.stopLoading());
    }
};

/**
 * Retrieves partners by their IDs. This is optimized for users who are linked 
 * with multiple partners and only need to fetch the specific partners they have access to.
 * @param partnerIDs - Array of partner IDs to retrieve.
 * @returns Promise<Partner[]> The list of partners that were found.
 */
const getPartnersByIds = (partnerIDs: string[]) => async (dispatch: AppDispatch) => {
    dispatch(PartnersActions.startLoadingList());

    try {
        if (!partnerIDs || partnerIDs.length === 0) {
            dispatch(PartnersActions.setList([]));
            return [];
        }

        const db = getFirestore();
        const partnersRef = collection(db, DbCollection.PARTNERS);
        
        // Firestore 'in' queries are limited to 10 items, so we need to batch if more than 10
        const batchSize = 10;
        const batches: Promise<Partner[]>[] = [];
        
        for (let i = 0; i < partnerIDs.length; i += batchSize) {
            const batch = partnerIDs.slice(i, i + batchSize);
            const batchQuery = query(partnersRef, where('__name__', 'in', batch));
            
            const batchPromise = getDocs(batchQuery).then(snapshot => 
                snapshot.docs.map(doc => {
                    const data = doc.data() as PartnerDbData;
                    const viewport = data.defaultMapViewport;
                    return {
                        ...data,
                        ID: doc.id,
                        defaultMapViewport: viewport ? {
                            center: { latitude: viewport.center.latitude, longitude: viewport.center.longitude },
                            zoom: viewport.zoom,
                        } : undefined,
                    } as Partner;
                })
            );
            
            batches.push(batchPromise);
        }

        // Wait for all batches to complete and flatten the results
        const batchResults = await Promise.all(batches);
        const partners = batchResults.flat();

        // Sort partners by name for better UX
        partners.sort((a, b) => a.name.localeCompare(b.name));

        dispatch(PartnersActions.setList(partners));
        return partners;
    } catch (e) {
        const error = e as Error;
        console.error("Error fetching partners by IDs:", e);
        dispatch(PartnersActions.setError(error.message));
        dispatch(showError(error.message));
        return [];
    }
};

/**
 * Selects a partner and applies the partner's sorting rules to the app.
 */
const select = (partner: Partner) => (dispatch: AppDispatch) => {
    dispatch(PartnerActions.setPartner(partner));
    dispatch(SortingRulesActions.setRules(partner.sortingRules)); // apply the partner's sorting rules to the app
};

const PartnersController = {
    listPartners,
    fetchPartnerDoc,
    getPartnersByIds,
    select,
};

export default PartnersController;